import { useState } from "react";
import { opApi, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { UserPlus } from "lucide-react";

const sel = "w-full border border-input bg-background h-9 px-2 text-sm mt-1";

export function WalkInButton({ services, providers, viewer, onAdded }) {
  const bookable = viewer.manage || viewer.shared ? providers : providers.filter((p) => p.id === viewer.providerId);
  const blank = () => ({ service_id: services[0]?.id || "", provider_id: viewer.providerId || bookable[0]?.id || "", customer_name: "", customer_phone: "", customer_email: "", start: "", date: "" });
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  if (!bookable.length) return null;
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async () => {
    setSaving(true);
    try {
      const b = { ...f }; if (!b.start) delete b.start; if (!b.date) delete b.date;
      const { data } = await opApi.post("/tenant/appointments/walk-in", b);
      toast.success(data.status === "checked_in" ? `${data.customer.name} checked in with ${data.provider_name}` : `Booked ${data.customer.name} with ${data.provider_name}`);
      setF(null); onAdded && onAdded();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <>
      <Button className="rounded-none gap-1.5" onClick={() => setF(blank())} data-testid="walkin-btn"><UserPlus className="h-4 w-4" /> Walk-in</Button>
      <Dialog open={!!f} onOpenChange={(o) => !o && setF(null)}>
        <DialogContent className="rounded-none" data-testid="walkin-dialog">
          <DialogHeader><DialogTitle>Add a walk-in</DialogTitle></DialogHeader>
          {f && (
            <div className="space-y-3">
              <div><Label>Service</Label><select className={sel} value={f.service_id} onChange={(e) => set("service_id", e.target.value)} data-testid="walkin-service">{services.map((s) => <option key={s.id} value={s.id}>{`${s.name} · ${s.duration_min} min`}</option>)}</select></div>
              <div><Label>With</Label><select className={sel} value={f.provider_id} onChange={(e) => set("provider_id", e.target.value)} data-testid="walkin-provider">{bookable.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
              <div><Label>Client name</Label><Input className="rounded-none mt-1" value={f.customer_name} onChange={(e) => set("customer_name", e.target.value)} data-testid="walkin-name" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Phone</Label><Input className="rounded-none mt-1" value={f.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} data-testid="walkin-phone" /></div>
                <div><Label>Email</Label><Input className="rounded-none mt-1" value={f.customer_email} onChange={(e) => set("customer_email", e.target.value)} data-testid="walkin-email" /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Date <span className="text-muted-foreground font-normal">(blank = today)</span></Label><Input type="date" className="rounded-none mt-1" value={f.date} onChange={(e) => set("date", e.target.value)} data-testid="walkin-date" /></div>
                <div><Label>Time <span className="text-muted-foreground font-normal">(blank = now)</span></Label><Input type="time" className="rounded-none mt-1" value={f.start} onChange={(e) => set("start", e.target.value)} data-testid="walkin-time" /></div>
              </div>
              <p className="text-xs text-muted-foreground">Starting now checks them in right away. A later time books it on the calendar.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" className="rounded-none" onClick={() => setF(null)} data-testid="walkin-cancel">Cancel</Button>
            <Button className="rounded-none" onClick={save} disabled={saving} data-testid="walkin-save">{saving ? "Adding…" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
